/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import {
  DataContextProvider,
  IDataConnectorConfigRaw
} from '@forge/data-connector';
import { Card, Icon } from '@scuf/common';
import moment from 'moment';
import React from 'react';
import descriptors from '../../../../locales/en-US/descriptors';
import { ConversionType } from '../../../../main';
import {
  IAny,
  IDisplayTabs,
  IEmaasEnergyUsageComponentData,
  IPerformanceAggregationProps,
  IRequestContext
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import {
  baselineFields,
  consuptionFields,
  IPortfolioEnergyData,
  IPortfolioEnergySite
} from '../../../DetailKPIWidgets/PortfolioEnergyUsageViewDetails/PremiumSubscription/IPortfolioEnergyViewDetails';
import {
  EmaasSubscriptionType,
  EnergyUsageDataSourceType,
  EnergyUsageField
} from '../../Enums';
import {
  EnergyViewDetailsAllUtilitiesFields,
  euiDefaultUnit,
  IntervalType,
  KPI_VALUE_DECIMAL_PRECISION,
  MAGIC_VALUE,
  maxDaysInMonth,
  maxDaysInYear,
  PERCENT_CHANGE_THRESHOLD,
  portfolioEnergyTabs,
  TEN_PERCENT_OF
} from '../Constants';
import {
  getEnergyEndDateBasedOnSub,
  getEnergyStartDate
} from '../EnergyUsagesViewDetailsUtils';
import { handleEuiUnitConversion } from '../unitHandling/EuiUnitHandling';
import {
  getConvertedValue,
  getMaxConvertedValueFromArray
} from '../unitHandling/unitHandling';
import {
  conversionCheck,
  maxValBDBSCheck,
  maxConsupValueCheck,
  maxEUIValCheck,
  maxEuiValueCheck,
  handleUnitConversion
} from '../Utils';
import SharedTabsWidget from '../../SharedTabs/SharedTabs';
import { UnitUtility } from '@bmsp/bmsp.web.component.common';
import _ from 'lodash';

export const GetHeader = (tabName: string | undefined) => {
  const descriptor = descriptors.energyManagement;
  if (tabName === 'allutilities') return descriptor.ENERGYPERFORMANCEALL;
  else if (tabName === 'electricity') return descriptor.ELECTRICITYPERFORMANCE;
  else if (tabName === 'gas') return descriptor.GASPERFORMANCEDETAILS;
  else return descriptor.WaterOverview;
};
export const GetTableHeader = (tabName: string | undefined) => {
  const descriptor = descriptors.energyManagement;
  if (tabName === 'allutilities') return descriptor.ENERGYUSAGEBYSITE;
  else if (tabName === 'electricity') return descriptor.ELECTRICITYUSAGEBYSITE;
  else if (tabName === 'gas') return descriptor.GASUSAGEBYSITE;
  else return descriptor.WATERUSAGEBYSITE;
};

const getEuiDeviation = (
  site: any,
  euiDefaultUnit: any,
  preferredEuiUnit: any
) => {
  return site.actualEui
    ? handleEuiUnitConversion(
        site.actualEui,
        euiDefaultUnit,
        preferredEuiUnit || ''
      )
    : null;
};

export const getPortfolioEnergyData = (
  portfolioData: IPortfolioEnergyData,
  onSiteclick: (siteId: string) => void,
  unit: string,
  preferredEuiUnit?: string
) => {
  const conversionType = conversionCheck(portfolioData.sourceType);
  let {
    summary: { totalConsumption, totalBaselineConsumption },
    breakdownBySite,
    energyMeasurementUnit,
    subscriptionType
  } = portfolioData;
  let { displayUnit } = EnergyViewDetailsAllUtilitiesFields;
  let maxArray: (number | null)[] = [];
  let actualFieldForProgressbar: consuptionFields = 'totalConsumption',
    baselineFieldForProgressbar: baselineFields = 'totalBaselineConsumption';
  if (subscriptionType === EnergyUsageField.PREMIUMSUBS) {
    actualFieldForProgressbar = 'absoluteTotalConsumption';
    baselineFieldForProgressbar = 'absoluteTotalBaselineConsumption';
  }

  let maxConsupValueforProgressBar: number | null = Number.MIN_VALUE;
  let maxEuiValueforProgressBar: number | null = Number.MIN_VALUE;
  breakdownBySite.forEach((site) => {
    maxArray.push(site.totalBaselineConsumption, site.totalConsumption);

    const maxVal = maxValBDBSCheck(
      site,
      actualFieldForProgressbar,
      baselineFieldForProgressbar
    );

    maxConsupValueforProgressBar = maxConsupValueCheck(
      maxVal,
      maxConsupValueforProgressBar
    );

    if (
      site.actualEui !== null &&
      site.baselineEui !== null &&
      maxEuiValueforProgressBar !== null
    ) {
      const maxEUIVal = maxEUIValCheck(site.actualEui, site.baselineEui);
      maxEuiValueforProgressBar = maxEuiValueCheck(
        maxEUIVal,
        maxEuiValueforProgressBar
      );
    }
  });
  const converToUnit = getMaxConvertedValueFromArray(
    conversionType,
    conversionType.toLowerCase() === 'gas' ? energyMeasurementUnit : unit, //energyMeasurementUnit,
    maxArray,
    energyMeasurementUnit
  );
  maxConsupValueforProgressBar = handleUnitConversion(
    maxConsupValueforProgressBar,
    conversionType,
    energyMeasurementUnit,
    converToUnit.Unit
  );
  breakdownBySite = breakdownBySite.map((site: IPortfolioEnergySite) => {
    let siteData;
    siteData = {
      ...site,
      totalBaselineConsumption: site.totalBaselineConsumption,
      actualEui: getEuiDeviation(site, euiDefaultUnit, preferredEuiUnit || ''),
      baselineEui: site.baselineEui
        ? handleEuiUnitConversion(
            site.baselineEui,
            euiDefaultUnit,
            preferredEuiUnit || ''
          )
        : null,
      euiDeviation: site.euiDeviation
        ? handleEuiUnitConversion(
            site.euiDeviation,
            euiDefaultUnit,
            preferredEuiUnit || ''
          )
        : null,
      totalConsumption: site.totalConsumption,
      totalEnergyDeviation: site.totalEnergyDeviation,
      // relativeProgressWidth: maxConsupValueforProgressBar,
      relativeEuiWidth: maxEuiValueforProgressBar,
      [displayUnit]: converToUnit.Unit,
      absoluteTotalBaselineConsumption: site[baselineFieldForProgressbar],
      absoluteTotalConsumption: site[actualFieldForProgressbar],
      onSiteclick
    };
    // if (
    //   siteData.totalEnergyDeviation === 0 &&
    //   site.totalEnergyDeviation !== 0
    // ) {
    //   siteData = {
    //     ...siteData,
    //     deviationScaledData: getConvertedValue(
    //       conversionType,
    //       energyMeasurementUnit,
    //       site.totalEnergyDeviation,
    //       converToUnit.Unit
    //     )
    //   };
    // }
    return siteData;
  });

  return {
    convertedTableData: breakdownBySite,
    displayUnitAfterConversion: converToUnit.Unit
  };
};

export const getPortfolioEnergyDataWithPercentage = (
  portfolioData: IPortfolioEnergyData,
  onSiteclick: (siteId: string) => void,
  unit: string,
  preferredEuiUnit?: string,
  showChangeWhenData: boolean = false
) => {
  const conversionType = conversionCheck(portfolioData.sourceType);
  let {
    // summary: { totalConsumption, totalBaselineConsumption },
    breakdownBySite,
    energyMeasurementUnit,
    subscriptionType
  } = portfolioData;
  let { displayUnit } = EnergyViewDetailsAllUtilitiesFields;
  let maxArray: (number | null)[] = [];
  let actualFieldForProgressbar: consuptionFields = 'totalConsumption',
    baselineFieldForProgressbar: baselineFields = 'totalBaselineConsumption';
  if (
    subscriptionType === EnergyUsageField.PREMIUMSUBS &&
    conversionType !== ConversionType.WATER
  ) {
    actualFieldForProgressbar = 'totalConsumption';
    baselineFieldForProgressbar = 'absoluteTotalBaselineConsumption';
  }

  let maxConsupValueforProgressBar: number | null = Number.MIN_VALUE;
  let maxEuiValueforProgressBar: number | null = Number.MIN_VALUE;
  breakdownBySite.forEach((site) => {
    maxArray.push(site.totalBaselineConsumption, site.totalConsumption);

    const maxVal = maxValBDBSCheck(
      site,
      actualFieldForProgressbar,
      baselineFieldForProgressbar
    );

    maxConsupValueforProgressBar = maxConsupValueCheck(
      maxVal,
      maxConsupValueforProgressBar
    );

    if (
      site.actualEui !== null &&
      site.baselineEui !== null &&
      maxEuiValueforProgressBar !== null
    ) {
      const maxEUIVal = maxEUIValCheck(site.actualEui, site.baselineEui);
      maxEuiValueforProgressBar = maxEuiValueCheck(
        maxEUIVal,
        maxEuiValueforProgressBar
      );
    }
  });
  const converToUnit = getMaxConvertedValueFromArray(
    conversionType,
    conversionType.toLowerCase() === 'gas' ? energyMeasurementUnit : unit, //energyMeasurementUnit,
    maxArray,
    energyMeasurementUnit
  );
  maxConsupValueforProgressBar = handleUnitConversion(
    maxConsupValueforProgressBar,
    conversionType,
    energyMeasurementUnit,
    converToUnit.Unit
  );
  breakdownBySite = breakdownBySite.map((site: IPortfolioEnergySite) => {
    site.isCompleted =
      subscriptionType === EnergyUsageField.BASICSUBS ? site.isCompleted : true;
    let siteData;
    const savingPercent =
      showChangeWhenData &&
      (isNaN(Number(site.totalBaselineConsumption)) ||
      isNaN(Number(site.totalConsumption)) ||
      Number(site.totalConsumption) === 0 ||
      Number(site.totalBaselineConsumption) === 0 ||
      !site.isCompleted ||
      Math.abs(Number(site.totalEnergyDeviationPercentage)) >
        PERCENT_CHANGE_THRESHOLD
        ? true
        : false);
    siteData = {
      ...site,
      totalBaselineConsumption: site.totalBaselineConsumption,
      actualEui: getEuiDeviation(site, euiDefaultUnit, preferredEuiUnit || ''),
      baselineEui: site.baselineEui
        ? handleEuiUnitConversion(
            site.baselineEui,
            euiDefaultUnit,
            preferredEuiUnit || ''
          )
        : null,
      euiDeviation:
        site.baselineEui &&
        site.actualEui &&
        Number(site.actualEui) !== 0 &&
        Number(site.baselineEui) !== 0
          ? site.euiDeviation
            ? handleEuiUnitConversion(
                site.euiDeviation,
                euiDefaultUnit,
                preferredEuiUnit || ''
              )
            : null
          : null,
      totalConsumption: site.totalConsumption,
      totalEnergyDeviation: savingPercent ? null : site.totalEnergyDeviation,
      // relativeProgressWidth: maxConsupValueforProgressBar,
      relativeEuiWidth: maxEuiValueforProgressBar,
      [displayUnit]: converToUnit.Unit,
      baselineUnit: getMaxConvertedValueFromArray(
        conversionType,
        unit, //energyMeasurementUnit,
        [site.totalBaselineConsumption],
        energyMeasurementUnit
      ).Unit,
      actualUnit: getMaxConvertedValueFromArray(
        conversionType,
        unit, //energyMeasurementUnit,
        [site.totalConsumption],
        energyMeasurementUnit
      ).Unit,
      deviationUnit: getMaxConvertedValueFromArray(
        conversionType,
        unit, //energyMeasurementUnit,
        [site.totalEnergyDeviation],
        energyMeasurementUnit
      ).Unit,
      changeUnit: getMaxConvertedValueFromArray(
        conversionType,
        unit, //energyMeasurementUnit,
        [site.totalEnergyDeviation],
        energyMeasurementUnit
      ).Unit,
      absoluteTotalBaselineConsumption: site[baselineFieldForProgressbar],
      absoluteTotalConsumption: site[actualFieldForProgressbar],
      onSiteclick
    };
    // if (
    //   siteData.totalEnergyDeviation === 0 &&
    //   site.totalEnergyDeviation !== 0
    // ) {
    //   siteData = {
    //     ...siteData,
    //     deviationScaledData: getConvertedValue(
    //       conversionType,
    //       energyMeasurementUnit,
    //       site.totalEnergyDeviation,
    //       converToUnit.Unit
    //     )
    //   };
    // }
    return siteData;
  });

  return {
    convertedTableData: breakdownBySite,
    displayUnitAfterConversion: converToUnit.Unit
  };
};

export const getToUnitForChart = (
  performanceAggregation: IPerformanceAggregationProps[],
  conversionType: string,
  fromUnit: string
) => {
  let valuesArrayForConversion: (number | null)[] = [];
  performanceAggregation &&
    performanceAggregation.length &&
    performanceAggregation.forEach(
      (agrregation: IPerformanceAggregationProps) => {
        valuesArrayForConversion.push(
          agrregation.consumption,
          agrregation.baselineConsumption
        );
      }
    );
  const { Unit } = getMaxConvertedValueFromArray(
    conversionType,
    fromUnit,
    valuesArrayForConversion
  );
  return Unit;
};
export const getMaxYAxisValue = (
  actualConsumptionData: [number, number][],
  baselineConsumptionData: [number, number][]
) => {
  let maxValue = Number.MIN_VALUE;
  actualConsumptionData
    .concat(baselineConsumptionData)
    .forEach((consupvalue: [number, number]) => {
      maxValue = maxValue > consupvalue[1] ? maxValue : consupvalue[1];
    });
  return (maxValue + TEN_PERCENT_OF * maxValue) / MAGIC_VALUE.THREE;
};

export const getMaxYAxisValueForOat = (oatSeries: [number, number][]) => {
  let maxValue = Number.MIN_VALUE;
  oatSeries &&
    oatSeries.length &&
    oatSeries.forEach((consupvalue: [number, number]) => {
      maxValue = maxValue > consupvalue[1] ? maxValue : consupvalue[1];
    });
  return (maxValue + TEN_PERCENT_OF * maxValue) / MAGIC_VALUE.THREE;
};

export const getYAxisLabel = (unit: string, ConsuppValue: number) => {
  let decimalPrecision = 1;
  if (ConsuppValue < 1) {
    decimalPrecision = KPI_VALUE_DECIMAL_PRECISION;
  }
  const formattedNumber = Number.isInteger(ConsuppValue)
    ? ConsuppValue
    : ConsuppValue.toFixed(decimalPrecision);
  return `${formattedNumber} ${unit}`;
};

export const getYAxisLabelForOat = (unit: string, ConsuppValue: number) => {
  let decimalPrecision = 1;
  if (ConsuppValue < 1) {
    decimalPrecision = KPI_VALUE_DECIMAL_PRECISION;
  }
  const formattedNumber = Number.isInteger(ConsuppValue)
    ? ConsuppValue
    : ConsuppValue.toFixed(decimalPrecision);
  return `${formattedNumber}${unit}`;
};

export const getIntervalType = (
  startDate: string,
  endDate: string = new Date().toISOString()
) => {
  const startDay = moment(startDate);
  const endDay = moment(moment(endDate).format('YYYY-MM-DD'));
  let daysInterval = endDay.diff(startDay, 'days');
  if (daysInterval < 1) return IntervalType.Hourly;
  else if (daysInterval <= maxDaysInYear) return IntervalType.Daily;
  else return IntervalType.Monthly;
};

export const getIntervalTypeForEnergyDisaggregation = (
  startDate: string,
  endDate: string
) => {
  const startDay = moment(startDate);
  const endDay = moment(endDate);
  let daysInterval = endDay.diff(startDay, 'days');
  if (daysInterval < MAGIC_VALUE.ONE) return IntervalType.Hourly;
  else if (daysInterval <= MAGIC_VALUE.THIRTYONE) return IntervalType.Daily;
  else return IntervalType.Monthly;
};

export const getEnergyEndDate = () => {
  return moment(new Date()).format('YYYY-MM-DD');
};

export const getEndDateForBasicSubscription = () => {
  return moment(new Date())
    .subtract(1, 'month')
    .format('YYYY-MM-DD');
};

export const getPortfolioVDConnectorConfig = (
  requestContext: IRequestContext,
  utilityType: string
) => {
  const startDate = getEnergyStartDate(
    requestContext.context.startTime!,
    requestContext.context.subscriptionType!,
    undefined,
    undefined,
    requestContext?.context?.dataSource ===
      EnergyUsageDataSourceType.UTILITY_METER
  );
  const endDate = getEnergyEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!
  );
  return {
    name: 'portfolio-energy-usage',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      utilityType: utilityType, //'All',
      dataSource: requestContext?.context?.dataSource || '',
      intervalType:
        utilityType === portfolioEnergyTabs.Water
          ? IntervalType.Monthly
          : getIntervalType(startDate, endDate)
    }
  };
};

export const getEnergyTabName = (tabName: string) => {
  if (tabName === portfolioEnergyTabs.All) return 'allutilities';
  else if (tabName === portfolioEnergyTabs.Electric) return 'electricity';
  else if (tabName === portfolioEnergyTabs.Gas) return 'gas';
  else if (tabName === portfolioEnergyTabs.Water) return 'water';
  else return '';
};

export const formatCommonEnergyDataForNILM = (
  data: IEmaasEnergyUsageComponentData,
  fromUnit: string,
  showChangeWhenData: boolean = false,
  scaleSeparately: boolean = false
) => {
  const alternateSummary = {
    totalConsumption: null,
    totalBaselineConsumption: null,
    absoluteTotalConsumption: null,
    absoluteTotalBaselineConsumption: null,
    totalEnergyDeviation: null,
    totalEnergyDeviationPercentage: null
  };
  const { nilmAggregation, energyMeasurementUnit, sourceType } = data;
  const {
    totalBaselineConsumption,
    totalConsumption,
    totalEnergyDeviation,
    totalEnergyDeviationPercentage
  } =
    nilmAggregation && nilmAggregation.summary
      ? nilmAggregation.summary
      : alternateSummary;
  // const aggregationsEndDate =  getEnergyEndDate();
  const aggregationsEndDate = data.aggregationsEndDate;

  const valuesArray = [totalConsumption, totalBaselineConsumption];

  let conversionType = ConversionType.ELECTRICITY;
  if (sourceType == 'Gas') {
    conversionType = ConversionType.GAS;
  } else if (sourceType == 'Water') {
    conversionType = ConversionType.WATER;
  }
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    conversionType,
    conversionType.toLowerCase() === 'gas' ||
      conversionType.toLowerCase() === 'water'
      ? energyMeasurementUnit
      : fromUnit,
    valuesArray
  );
  const aggregationSummaryData = {
    actualCost: scaleSeparately
      ? getConvertedValue(
          conversionType,
          energyMeasurementUnit,
          totalConsumption,
          getMaxConvertedValueFromArray(
            conversionType,
            conversionType.toLowerCase() === 'gas'
              ? energyMeasurementUnit
              : fromUnit, //energyMeasurementUnit,
            [totalConsumption]
          ).Unit
        ).Value
      : getConvertedValue(
          conversionType,
          energyMeasurementUnit,
          totalConsumption,
          maxValueUnitConversion.Unit
        ).Value,
    actualUnit: getMaxConvertedValueFromArray(
      conversionType,
      conversionType.toLowerCase() === 'gas' ? energyMeasurementUnit : fromUnit, //energyMeasurementUnit,
      [totalConsumption]
    ).Unit,
    baselineCost: scaleSeparately
      ? getConvertedValue(
          conversionType,
          energyMeasurementUnit,
          totalBaselineConsumption,
          getMaxConvertedValueFromArray(
            conversionType,
            conversionType.toLowerCase() === 'gas'
              ? energyMeasurementUnit
              : fromUnit, //energyMeasurementUnit,
            [totalBaselineConsumption]
          ).Unit
        ).Value
      : getConvertedValue(
          conversionType,
          energyMeasurementUnit,
          totalBaselineConsumption,
          maxValueUnitConversion.Unit
        ).Value,
    baselineUnit: getMaxConvertedValueFromArray(
      conversionType,
      conversionType.toLowerCase() === 'gas' ? energyMeasurementUnit : fromUnit, //energyMeasurementUnit,
      [totalBaselineConsumption]
    ).Unit,
    actualAvoidanceCost: null,
    baselineAvoidanceCost: null,
    costSavings:
      !isNaN(Number(totalBaselineConsumption)) &&
      !isNaN(Number(totalConsumption)) &&
      Number(totalConsumption) !== 0 &&
      Number(totalBaselineConsumption) !== 0 &&
      Math.abs(Number(totalEnergyDeviationPercentage)) <=
        PERCENT_CHANGE_THRESHOLD
        ? scaleSeparately
          ? getConvertedValue(
              conversionType,
              energyMeasurementUnit,
              totalEnergyDeviation,
              getMaxConvertedValueFromArray(
                conversionType,
                conversionType.toLowerCase() === 'gas'
                  ? energyMeasurementUnit
                  : fromUnit, //energyMeasurementUnit,
                [totalEnergyDeviation]
              ).Unit
            ).Value
          : getConvertedValue(
              conversionType,
              energyMeasurementUnit,
              totalEnergyDeviation,
              maxValueUnitConversion.Unit
            ).Value
        : showChangeWhenData
        ? null
        : scaleSeparately
        ? getConvertedValue(
            conversionType,
            energyMeasurementUnit,
            totalEnergyDeviation,
            getMaxConvertedValueFromArray(
              conversionType,
              conversionType.toLowerCase() === 'gas'
                ? energyMeasurementUnit
                : fromUnit, //energyMeasurementUnit,
              [totalEnergyDeviation]
            ).Unit
          ).Value
        : getConvertedValue(
            conversionType,
            energyMeasurementUnit,
            totalEnergyDeviation,
            maxValueUnitConversion.Unit
          ).Value,
    changeUnit: getMaxConvertedValueFromArray(
      conversionType,
      conversionType.toLowerCase() === 'gas' ? energyMeasurementUnit : fromUnit, //energyMeasurementUnit,
      [totalEnergyDeviation]
    ).Unit,
    costSavingsPercentage: parseFloat(
      totalEnergyDeviationPercentage!?.toFixed(KPI_VALUE_DECIMAL_PRECISION)
    )
  };
  let deviationScaledData;
  if (aggregationSummaryData.costSavings === 0 && totalEnergyDeviation !== 0) {
    deviationScaledData = getConvertedValue(
      conversionType,
      energyMeasurementUnit,
      totalEnergyDeviation,
      maxValueUnitConversion.Unit
    );
  }
  return {
    conversionType,
    aggregationSummaryData,
    maxValueUnitConversion,
    aggregationsEndDate,
    deviationScaledData
  };
};

export const formatCommonEnergyData = (
  data: IEmaasEnergyUsageComponentData,
  fromUnit: string,
  showChangeWhenData: boolean = false,
  scaleSeparately: boolean = false
) => {
  const {
    energyMeasurementUnit,
    summary,
    sourceType,
    aggregationsEndDate
  } = data;
  const {
    totalBaselineConsumption,
    totalConsumption,
    totalEnergyDeviation,
    totalEnergyDeviationPercentage,
    avgDailyConsumption,
    avgMonthlyConsumption,
    monthlyConsumptionPersqft
  } = summary;

  const valuesArray = [totalConsumption, totalBaselineConsumption];

  let conversionType = ConversionType.ELECTRICITY;
  if (sourceType == 'Gas') {
    conversionType = ConversionType.GAS;
  }
  if (sourceType == 'Water') {
    conversionType = ConversionType.WATER;
  }
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    conversionType,
    fromUnit, //energyMeasurementUnit,
    valuesArray,
    energyMeasurementUnit
  );

  const changePercent = totalEnergyDeviationPercentage!?.toFixed(
    KPI_VALUE_DECIMAL_PRECISION
  );
  const aggregationSummaryData = {
    actualCost: scaleSeparately
      ? getConvertedValue(
          conversionType,
          energyMeasurementUnit,
          totalConsumption,
          getMaxConvertedValueFromArray(
            conversionType,
            fromUnit, //energyMeasurementUnit,
            [totalConsumption],
            energyMeasurementUnit
          ).Unit
        ).Value
      : getConvertedValue(
          conversionType,
          energyMeasurementUnit,
          totalConsumption,
          maxValueUnitConversion.Unit
        ).Value,
    actualUnit: getMaxConvertedValueFromArray(
      conversionType,
      fromUnit, //energyMeasurementUnit,
      [totalConsumption],
      energyMeasurementUnit
    ).Unit,
    baselineCost: scaleSeparately
      ? getConvertedValue(
          conversionType,
          energyMeasurementUnit,
          totalBaselineConsumption,
          getMaxConvertedValueFromArray(
            conversionType,
            fromUnit, //energyMeasurementUnit,
            [totalBaselineConsumption],
            energyMeasurementUnit
          ).Unit
        ).Value
      : getConvertedValue(
          conversionType,
          energyMeasurementUnit,
          totalBaselineConsumption,
          maxValueUnitConversion.Unit
        ).Value,
    baselineUnit: getMaxConvertedValueFromArray(
      conversionType,
      fromUnit, //energyMeasurementUnit,
      [totalBaselineConsumption],
      energyMeasurementUnit
    ).Unit,
    actualAvoidanceCost: null,
    baselineAvoidanceCost: null,
    costSavings:
      !isNaN(Number(totalBaselineConsumption)) &&
      !isNaN(Number(totalConsumption)) &&
      Number(totalConsumption) !== 0 &&
      Number(totalBaselineConsumption) !== 0 &&
      Math.abs(Number(changePercent)) <= PERCENT_CHANGE_THRESHOLD
        ? scaleSeparately
          ? getConvertedValue(
              conversionType,
              energyMeasurementUnit,
              totalEnergyDeviation,
              getMaxConvertedValueFromArray(
                conversionType,
                fromUnit, //energyMeasurementUnit,
                [totalEnergyDeviation],
                energyMeasurementUnit
              ).Unit
            ).Value
          : getConvertedValue(
              conversionType,
              energyMeasurementUnit,
              totalEnergyDeviation,
              maxValueUnitConversion.Unit
            ).Value
        : showChangeWhenData
        ? null
        : scaleSeparately
        ? getConvertedValue(
            conversionType,
            energyMeasurementUnit,
            totalEnergyDeviation,
            getMaxConvertedValueFromArray(
              conversionType,
              fromUnit, //energyMeasurementUnit,
              [totalEnergyDeviation],
              energyMeasurementUnit
            ).Unit
          ).Value
        : getConvertedValue(
            conversionType,
            energyMeasurementUnit,
            totalEnergyDeviation,
            maxValueUnitConversion.Unit
          ).Value,
    changeUnit: getMaxConvertedValueFromArray(
      conversionType,
      fromUnit, //energyMeasurementUnit,
      [totalEnergyDeviation],
      energyMeasurementUnit
    ).Unit,
    costSavingsPercentage: parseFloat(
      totalEnergyDeviationPercentage!?.toFixed(KPI_VALUE_DECIMAL_PRECISION)
    ),
    // #TODO Water Meter remove undefined during api integration
    avgDailyConsumption: getConvertedValue(
      conversionType,
      energyMeasurementUnit,
      _.isUndefined(avgDailyConsumption) ? null : avgDailyConsumption,
      getMaxConvertedValueFromArray(
        conversionType,
        fromUnit, //energyMeasurementUnit,
        [_.isUndefined(avgDailyConsumption) ? null : avgDailyConsumption],
        energyMeasurementUnit
      ).Unit
    ).Value,
    avgDailyConsumptionUnit: getMaxConvertedValueFromArray(
      conversionType,
      fromUnit, //energyMeasurementUnit,
      [_.isUndefined(avgDailyConsumption) ? null : avgDailyConsumption],
      energyMeasurementUnit
    ).Unit,
    avgMonthlyConsumption: getConvertedValue(
      conversionType,
      energyMeasurementUnit,
      _.isUndefined(avgMonthlyConsumption) ? null : avgMonthlyConsumption,
      getMaxConvertedValueFromArray(
        conversionType,
        fromUnit, //energyMeasurementUnit,
        [_.isUndefined(avgMonthlyConsumption) ? null : avgMonthlyConsumption],
        energyMeasurementUnit
      ).Unit
    ).Value,
    avgMonthlyConsumptionUnit: getMaxConvertedValueFromArray(
      conversionType,
      fromUnit, //energyMeasurementUnit,
      [_.isUndefined(avgMonthlyConsumption) ? null : avgMonthlyConsumption],
      energyMeasurementUnit
    ).Unit,
    monthlyConsumptionPersqft: getConvertedValue(
      conversionType,
      energyMeasurementUnit,
      _.isUndefined(monthlyConsumptionPersqft)
        ? null
        : monthlyConsumptionPersqft,
      getMaxConvertedValueFromArray(
        conversionType,
        fromUnit, //energyMeasurementUnit,
        [
          _.isUndefined(monthlyConsumptionPersqft)
            ? null
            : monthlyConsumptionPersqft
        ],
        energyMeasurementUnit
      ).Unit
    ).Value,
    monthlyConsumptionPersqftUnit: getMaxConvertedValueFromArray(
      conversionType,
      fromUnit, //energyMeasurementUnit,
      [
        _.isUndefined(monthlyConsumptionPersqft)
          ? null
          : monthlyConsumptionPersqft
      ],
      energyMeasurementUnit
    ).Unit
  };
  let deviationScaledData;
  if (aggregationSummaryData.costSavings === 0 && totalEnergyDeviation !== 0) {
    deviationScaledData = getConvertedValue(
      conversionType,
      energyMeasurementUnit,
      totalEnergyDeviation,
      maxValueUnitConversion.Unit
    );
  }
  return {
    conversionType,
    aggregationSummaryData,
    maxValueUnitConversion,
    aggregationsEndDate,
    deviationScaledData
  };
};

export const renderEneryVDTabs = (
  connectorConfig: IDataConnectorConfigRaw,
  onBackarrowClick: () => void,
  displayTabs: IDisplayTabs,
  tabHeader: string,
  energyUsageMultipleCalls: boolean = false
) => {
  return (
    <div className="emaas-utility-container-kpi-card-common">
      <Card>
        <Card.Header className="emaas-utility-bill-viewDetails-kpi-header-container">
          <div className="emaas-utility-bill-viewDetails-kpi-card-title">
            <span className="utility-bill-backButton">
              <Icon
                root="common"
                name="arrow-left"
                size="medium"
                onClick={() => onBackarrowClick()}
              />
            </span>
            {tabHeader}
          </div>
        </Card.Header>
        <Card.Content className="emaas-utility-bill-viewDetails-kpi-content-container">
          {energyUsageMultipleCalls ? (
            <DataContextProvider config={connectorConfig}>
              <SharedTabsWidget {...displayTabs} />
            </DataContextProvider>
          ) : (
            <SharedTabsWidget {...displayTabs} />
          )}
        </Card.Content>
      </Card>
    </div>
  );
};
